/**
 * URL state.
 *
 * Query string, not hash — the hash stays reserved for in-page anchors, and
 * query params survive being unfurled by Slack and Twitter.
 *
 * Two rules make shared links usable:
 *   1. Only values that differ from the published default are serialised, so
 *      the common link stays clean.
 *   2. Overrides carry full input ids. `a=teacher_benefit_load:1.45` is
 *      self-documenting in a tweet; `a=tbl:1.45` is not. Legibility is the
 *      product.
 */

import type { Pack } from '../engine/schema.js';
import type { AppState, Mode } from './store.js';

const P = {
  scenario: 's',
  mode: 'v',
  overrides: 'a',
  metric: 'm',
  dataVersion: 'dv',
  project: 'p',
  outcomes: 'o',
} as const;

/**
 * Which jurisdiction the page is showing, and how the URL says so.
 *
 * Two forms are accepted because the tool has two deployment targets with
 * incompatible constraints. The hosted build wants `/pine-island`, which reads
 * well and unfurls well. The single-file archival build opens from `file://`,
 * where there is no server to map a path back to the document, so it needs
 * `?p=pine-island`. Resolution prefers the query form when both are present:
 * an explicit parameter should beat a directory name that happens to collide
 * with a slug.
 */
export interface Route {
  slug: string;
  /** True when the slug came from the path rather than the query string. */
  inPath: boolean;
  defaultSlug: string;
}

export function readRoute(known: readonly string[], defaultSlug: string): Route {
  const q = new URLSearchParams(window.location.search);

  const explicit = q.get(P.project);
  if (explicit && known.includes(explicit)) {
    return { slug: explicit, inPath: false, defaultSlug };
  }

  const seg = lastPathSegment(window.location.pathname);
  if (seg && known.includes(seg)) {
    return { slug: seg, inPath: true, defaultSlug };
  }

  return { slug: defaultSlug, inPath: false, defaultSlug };
}

function lastPathSegment(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const last = parts[parts.length - 1] ?? '';
  // Strip an extension so `/pine-island.html` still routes.
  return last.replace(/\.html?$/i, '');
}

/**
 * The pathname a given slug should live at, preserving whichever form the page
 * was loaded with. Switching projects rewrites the last segment rather than
 * appending, so `/pine-island` → `/some-other-place`, not `/pine-island/other`.
 */
function pathForSlug(slug: string, route: Route): string {
  const path = window.location.pathname;
  if (!route.inPath) return path;
  const cut = path.lastIndexOf('/');
  return cut === -1 ? `/${slug}` : `${path.slice(0, cut + 1)}${slug}`;
}

export function readUrl(pack: Pack, route: Route): AppState {
  const q = new URLSearchParams(window.location.search);

  const scenarioRaw = q.get(P.scenario);
  const scenarioId =
    scenarioRaw && pack.scenarios.some((s) => s.id === scenarioRaw)
      ? scenarioRaw
      : pack.meta.defaultScenario;

  const mode: Mode = q.get(P.mode) === 'analyst' ? 'analyst' : 'plain';

  const overrides: Record<string, number> = {};
  const raw = q.get(P.overrides);
  if (raw) {
    for (const pair of raw.split(',')) {
      const idx = pair.lastIndexOf(':');
      if (idx <= 0) continue;
      const id = pair.slice(0, idx);
      const value = Number(pair.slice(idx + 1));
      if (!Number.isFinite(value)) continue;
      // Silently drop overrides for inputs that no longer exist or are no
      // longer adjustable. The banner tells the reader something was dropped.
      const input = pack.inputs.find((i) => i.id === id);
      if (!input?.adjustable) continue;
      overrides[id] = value;
    }
  }

  const dvRaw = q.get(P.dataVersion);
  const dv = dvRaw ? Number(dvRaw) : null;

  const openDrawer = q.get(P.metric);

  // Outcome ids are kept legible for the same reason override ids are: a link
  // reading `o=water,teachers` says what the sender cared about. Unknown ids
  // are dropped at render, so a renamed outcome degrades rather than breaks.
  const outcomes = (q.get(P.outcomes) ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^[a-z][a-z0-9_]*$/.test(s));

  return {
    packSlug: route.slug,
    scenarioId,
    mode,
    overrides,
    outcomes: [...new Set(outcomes)],
    openDrawer: openDrawer || null,
    linkedDataVersion: dv !== null && Number.isFinite(dv) ? dv : null,
  };
}

export function serialize(pack: Pack, state: AppState, route: Route): string {
  const q = new URLSearchParams();

  // Only name the project when the URL cannot say it any other way: the path
  // already carries it, or it is the pack served at the bare domain.
  if (!route.inPath && state.packSlug !== route.defaultSlug) {
    q.set(P.project, state.packSlug);
  }

  if (state.scenarioId !== pack.meta.defaultScenario) {
    q.set(P.scenario, state.scenarioId);
  }
  if (state.mode !== 'plain') q.set(P.mode, state.mode);
  if (state.openDrawer) q.set(P.metric, state.openDrawer);
  // Pick order is meaningful — it drives the summary order — so this is not sorted.
  if (state.outcomes.length) q.set(P.outcomes, state.outcomes.join(','));

  const entries = Object.entries(state.overrides);
  if (entries.length > 0) {
    // Serialise at the precision the slider step implies, so parse → serialise
    // is idempotent and a shared link reproduces exactly.
    const parts = entries
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, v]) => {
        const input = pack.inputs.find((i) => i.id === id);
        const step = input?.adjustable?.step ?? 0.01;
        const decimals = decimalsFor(step);
        return `${id}:${v.toFixed(decimals)}`;
      });
    q.set(P.overrides, parts.join(','));
    // Only stamp the data version when the link encodes a non-default state
    // worth reconciling against a later correction.
    q.set(P.dataVersion, String(pack.meta.dataVersion));
  } else if (state.scenarioId !== pack.meta.defaultScenario) {
    q.set(P.dataVersion, String(pack.meta.dataVersion));
  }

  const path = pathForSlug(state.packSlug, route);
  // URLSearchParams percent-encodes the separators, which turns a link that
  // should read `o=water,teachers` into `o=water%2Cteachers`. Both characters
  // are legal unencoded in a query value (RFC 3986 §3.4), and a link a person
  // can read in a tweet is the whole point of this module.
  const s = q.toString().replace(/%2C/g, ',').replace(/%3A/g, ':');
  return s ? `${path}?${s}` : path;
}

export function writeUrl(
  pack: Pack,
  state: AppState,
  route: Route,
  history: 'push' | 'replace',
): void {
  const next = serialize(pack, state, route);
  if (history === 'push') window.history.pushState(null, '', next);
  else window.history.replaceState(null, '', next);
}

function decimalsFor(step: number): number {
  if (Number.isInteger(step)) return 0;
  const s = String(step);
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

/** Absolute URL for the current view, for the copy-link action. */
export function shareUrl(pack: Pack, state: AppState, route: Route): string {
  return window.location.origin + serialize(pack, state, route);
}
